export const POUR_OVER_MAX_SECONDS = 210;

export const ROASTS = [
  "Light",
  "Medium-Light",
  "Medium",
  "Medium-Dark",
  "Dark",
  "Omni Roast",
  "Not Sure",
];

export const GRINDS = [
  "Extra Fine",
  "Fine",
  "Medium-Fine",
  "Medium",
  "Medium-Coarse",
  "Coarse",
  "Extra Coarse",
];

// Recommended grind per brewer, used for advisories only (never blocks)
const RECOMMENDED_GRIND = {
  "Pour Over": "Medium-Fine",
  "Pour-Over Immersion": "Medium-Fine",
  "French Press": "Coarse",
  "AeroPress": "Medium-Fine",
  "Espresso": "Fine",
  "Moka Pot": "Fine",
  "South Indian Filter": "Fine",
};

export function grindAdvisory(brewer, grind) {
  const rec = RECOMMENDED_GRIND[brewer];
  if (!rec || grind === rec) return null;
  return `Most people use a ${rec} grind for ${brewer}. ${grind} is unusual here — feel free to experiment.`;
}

export function clamp(v, min, max) {
  if (Number.isNaN(v) || !Number.isFinite(v)) return min;
  return Math.min(max, Math.max(min, v));
}

export function round1(v) {
  return Math.round(v * 10) / 10;
}

// Deterministic step engine — mirrors backend generate_steps
export function generateSteps(brewer, coffee, water, yieldG) {
  const b = (brewer || "").trim().toLowerCase();
  let steps = [];
  if (b === "pour over") {
    const bloom = 30;
    const pour = Math.max(30, Math.round(water * 0.5));
    const drawdown = 30;
    const p1 = Math.floor(pour / 2);
    const p2 = pour - p1;
    const w60 = Math.round(water * 0.6);
    steps = [
      { label: "Bloom", instruction: `Pour ${Math.min(Math.round(coffee * 2), Math.round(water))} ml of water to saturate the grounds and let them bloom.`, seconds: bloom },
      { label: "First Pour", instruction: `Slowly pour in gentle circles up to ${w60} ml.`, seconds: p1 },
      { label: "Second Pour", instruction: `Continue pouring up to ${Math.round(water)} ml total.`, seconds: p2 },
      { label: "Drawdown", instruction: "Let the water draw down through the bed completely.", seconds: drawdown },
    ];
  } else if (b === "pour-over immersion" || b === "pour over immersion") {
    steps = [
      { label: "Add Water", instruction: `Pour all ${Math.round(water)} ml of water, making sure every coffee ground is wet.`, seconds: 20 },
      { label: "Steep", instruction: "Keep the brewer closed and let the coffee steep.", seconds: 120 },
      { label: "Release & Drawdown", instruction: "Open the brewer over your cup and let the coffee draw down completely.", seconds: 60 },
    ];
  } else if (b === "french press") {
    steps = [
      { label: "Add Water", instruction: `Add all ${Math.round(water)} ml of hot water and saturate the grounds.`, seconds: 15 },
      { label: "Steep", instruction: "Let the coffee steep undisturbed.", seconds: 225 },
      { label: "Break Crust", instruction: "Break the crust and skim off the foam.", seconds: 15 },
      { label: "Plunge & Serve", instruction: "Insert the plunger, press slowly, and serve immediately.", seconds: 15 },
    ];
  } else if (b === "aeropress") {
    steps = [
      { label: "Add Water", instruction: `Add ${Math.round(water)} ml of water to the grounds.`, seconds: 15 },
      { label: "Stir", instruction: "Stir gently to saturate all grounds.", seconds: 15 },
      { label: "Steep", instruction: "Let the coffee steep.", seconds: 90 },
      { label: "Press", instruction: "Press down slowly and evenly.", seconds: 30 },
    ];
  } else if (b === "espresso") {
    steps = [
      { label: "Pre-infusion", instruction: "Begin extraction at low pressure.", seconds: 6 },
      { label: "Extraction", instruction: `Extract to a yield of ${Math.round(yieldG || 36)} g.`, seconds: 21 },
    ];
  } else if (b === "moka pot") {
    steps = [
      { label: "Heat", instruction: "Place the assembled moka pot over low-to-medium heat with the lid open.", seconds: 240 },
      { label: "Watch the Flow", instruction: "When coffee begins flowing, keep the stream slow and steady. Reduce the heat if it sputters.", seconds: 60 },
      { label: "Stop Extraction", instruction: "Remove the moka pot from the heat when the stream turns pale, then cool the lower chamber under water.", seconds: 15 },
    ];
  } else if (b === "south indian filter") {
    const firstPour = Math.min(30, Math.round(water));
    steps = [
      { label: "Wet the Coffee", instruction: `Gently add ${firstPour} ml of hot water to wet all the coffee evenly.`, seconds: 30 },
      { label: "Complete the Pour", instruction: `Slowly add the remaining water, reaching ${Math.round(water)} ml in total.`, seconds: 20 },
      { label: "Decoction Drip", instruction: "Close the lid and allow the decoction to drip through without disturbing the filter.", seconds: 600 },
    ];
  }
  const total = steps.reduce((a, s) => a + s.seconds, 0);
  return { steps, total };
}

export function formatMMSS(secs) {
  const s = Math.max(0, Math.round(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export function timeLabel(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

export const isEspresso = (brewer) => (brewer || "").toLowerCase() === "espresso";

// Per-roast suggestion: lighter roast -> finer grind + hotter water; darker -> coarser + cooler.
const ROAST_OFFSET = {
  "Light": { grind: -1, temp: 2 },
  "Medium-Light": { grind: -1, temp: 1 },
  "Medium": { grind: 0, temp: 0 },
  "Medium-Dark": { grind: 1, temp: -1 },
  "Dark": { grind: 1, temp: -2 },
  "Omni Roast": { grind: 0, temp: 0 },
  "Not Sure": { grind: 0, temp: 0 },
};

export function roastSuggestion(roast, baseGrind, baseTemp) {
  const off = ROAST_OFFSET[roast] || { grind: 0, temp: 0 };
  let gi = GRINDS.indexOf(baseGrind);
  if (gi < 0) gi = GRINDS.indexOf("Medium");
  const grind = GRINDS[Math.min(GRINDS.length - 1, Math.max(0, gi + off.grind))];
  const temperature = clamp(Math.round(Number(baseTemp) + off.temp), 40, 100);
  return { grind, temperature };
}
